import type { BoxProps } from "@mui/material";
import { Box, Typography } from "@mui/material";
import { ErrorBoundary } from "@sentry/react";
import { last } from "lodash-es";
import type { FC } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import type { UUID } from "../../../../../utils/uuid";
import { PromptSuggestions } from "./co-pilot-prompt-suggestions";
import { InitialPromptForm } from "./forms/initial-prompt";
import { RefineInput } from "./forms/refine";
import { CoPilotHeader } from "./header";
import { LoadingMessage } from "./loading-message";
import { Requests } from "./requests";
import type { CoPilot as CoPilotMode, Selection } from "./use-co-pilot-types";

type CoPilotProps = {
  coPilot: CoPilotMode;
  onInsert?: (text: string) => void;
  onReplace?: (text: string) => void;
  selection?: Selection;
} & BoxProps;

export const CoPilot: FC<CoPilotProps> = ({
  coPilot,
  selection,
  onInsert,
  onReplace,
  ...props
}) => {
  const [scrollFade, setScrollFade] = useState(0);
  const [loading, setLoading] = useState(false);
  const requestsContainer = useRef<HTMLDivElement>(null);

  const handleRefine = useCallback(
    (prompt: string): void => {
      setLoading(true);
      coPilot.actions
        .refine?.(prompt, last(coPilot.history.requests))
        .then(() => {
          setLoading(false);
          setTimeout(() => {
            requestsContainer.current?.scrollTo?.({
              top: requestsContainer.current.scrollHeight,
              behavior: "smooth",
            });
          }, 50);
        })
        .catch(console.error);
    },
    [coPilot.actions, coPilot.history.requests, setLoading],
  );

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    setScrollFade(Math.min(scrollTop, 20));
  }, []);

  const isCreate = coPilot.history.requests.length === 0 && !selection;
  const isRefine = !!selection || coPilot.history.requests.length > 0;

  const handleUseSuggestion = useCallback(
    (id?: UUID) => {
      const suggestion = coPilot.actions.getSuggestion?.(id) || "";
      if (selection) {
        onReplace?.(suggestion);
      } else {
        onInsert?.(suggestion);
      }
    },
    [coPilot.actions, selection, onInsert, onReplace],
  );

  const suggestions = useMemo(
    () => [
      { label: "Improve", action: "improve" },
      { label: "Shorten", action: "shorten" },
      { label: "Lengthen", action: "lengthen" },
    ],
    [],
  );

  return (
    <Box display="flex" flexDirection="column" gap={2} p={2} {...props}>
      <ErrorBoundary
        fallback={
          <Typography variant="body1">
            It appears CoPilot isn’t working right now. Try again later, or contact your customer
            services representative.
          </Typography>
        }
      >
        <CoPilotHeader pt={0.5} flex="0 0 auto" />

        <>
          {isRefine && (
            <Box
              flex="1 1 auto"
              ref={requestsContainer}
              onScroll={handleScroll}
              pr={2}
              sx={(theme) => ({
                overflowY: "scroll",
                maskImage: `linear-gradient(to top, ${theme.palette.background.paper} calc(100% - ${scrollFade}px), transparent 100%)`,
              })}
            >
              {selection && (
                <>
                  <Typography variant="body2" mb={2} fontStyle="italic">
                    {selection?.section}
                  </Typography>
                  {coPilot.history.requests.length === 0 && (
                    <PromptSuggestions
                      onUsePrompt={handleRefine}
                      loading={loading}
                      suggestions={suggestions}
                    />
                  )}
                </>
              )}

              {coPilot.history.requests.length > 0 && (
                <Requests
                  onRefine={handleRefine}
                  onUseSuggestion={handleUseSuggestion}
                  loading={loading}
                  requests={coPilot.history.requests}
                  suggestions={suggestions}
                  mode={selection ? "replace" : "insert"}
                />
              )}
            </Box>
          )}

          <Box flex="0 0 auto">
            {isCreate && <InitialPromptForm onSubmit={coPilot.actions.query} />}

            {isRefine && (
              <>
                <LoadingMessage loading={loading} />
                <RefineInput loading={loading} selection={selection} onRefine={handleRefine} />
              </>
            )}
          </Box>
        </>
      </ErrorBoundary>
    </Box>
  );
};
